import { useEffect, useMemo } from 'react';
import { einvoiceTotals, formatCents, formatPercent, taxCodeByCode, taxCodeForRate, TAX_CODES, type ProductOption, type SalesInvoiceLine, type TaxMode } from '@kontor/shared';
import { bpText, centsText, milliText, parseBp, parseCents, parseMilli } from '../../money';
import { ProductPicker } from '../offers/ProductPicker';
import { useUnits } from '../offers/LinesEditor';
import '../offers/offers.css';

/**
 * Positionen einer Rechnung (KON-151). Wie der Editor der Angebote, aber mit Steuerschlüsseln statt Steuersätzen. Die Summen rechnet dieselbe Funktion wie der
 * Server beim Ausstellen (einvoiceTotals).
 */

export interface InvoiceLineDraft {
  key: string;
  itemId: string | null;
  orderLineId: string | null;
  title: string;
  description: string;
  qty: string;
  unit: string;
  price: string;
  discount: string;
  taxCode: string;
}

let counter = 0;
const newKey = () => `il${++counter}`;
export const emptyInvoiceLine = (taxMode: TaxMode, unit = 'Stk.'): InvoiceLineDraft => ({ key: newKey(), itemId: null, orderLineId: null, title: '', description: '', qty: '1', unit, price: '', discount: '', taxCode: taxMode === 'small_business' ? 'KU' : 'S19' });
export const draftFromInvoiceLine = (l: SalesInvoiceLine): InvoiceLineDraft => ({ key: newKey(), itemId: l.itemId, orderLineId: l.orderLineId, title: l.title, description: l.description ?? '', qty: milliText(l.quantityMilli), unit: l.unit, price: centsText(l.unitPriceCents), discount: bpText(l.discountBp), taxCode: l.taxCode });
const draftFromProduct = (p: ProductOption, taxMode: TaxMode): InvoiceLineDraft => ({ key: newKey(), itemId: p.id, orderLineId: null, title: p.name, description: p.description ?? '', qty: '1', unit: p.unitSymbol, price: centsText(p.salePriceCents), discount: '', taxCode: taxCodeForRate(p.taxRate, taxMode) });

function parsed(lines: InvoiceLineDraft[]) {
  return lines.map((l) => ({ quantityMilli: parseMilli(l.qty), unitPriceCents: parseCents(l.price), discountBp: parseBp(l.discount || '0') }));
}

/** Positionen für den Aufruf der API */
export function invoiceLinesBody(lines: InvoiceLineDraft[]) {
  const p = parsed(lines);
  return lines.map((l, i) => ({
    itemId: l.itemId,
    orderLineId: l.orderLineId,
    title: l.title.trim(),
    description: l.description.trim() || null,
    quantityMilli: p[i]!.quantityMilli ?? 0,
    unit: l.unit.trim(),
    unitPriceCents: p[i]!.unitPriceCents ?? 0,
    discountBp: p[i]!.discountBp ?? 0,
    taxCode: l.taxCode,
  }));
}

/** Fehlertext zur ersten unvollständigen Position oder null */
export function invoiceLinesProblem(lines: InvoiceLineDraft[], unitSymbols: string[] | null): string | null {
  const p = parsed(lines);
  const bad = p.findIndex((x, i) => x.quantityMilli === null || x.quantityMilli <= 0 || x.unitPriceCents === null || !lines[i]!.title.trim());
  if (bad >= 0) return `Position ${bad + 1}: Bitte Bezeichnung, eine Menge größer als 0 und einen Preis angeben.`;
  const badUnit = unitSymbols ? lines.findIndex((l) => !unitSymbols.includes(l.unit)) : -1;
  if (badUnit >= 0) return `Position ${badUnit + 1}: Bitte eine Einheit aus der Auswahl wählen.`;
  return null;
}

/** Summen wie in der E-Rechnung. Gibt es keine gültigen Positionen, sind alle Werte 0. */
export function invoiceTotalsOf(lines: { quantityMilli: number; unitPriceCents: number; discountBp: number; taxCode: string }[], documentDiscountBp: number) {
  return einvoiceTotals({
    documentDiscountBp,
    prepayments: [],
    lines: lines.map((l, i) => {
      const code = taxCodeByCode(l.taxCode) ?? TAX_CODES[0]!;
      return { id: String(i + 1), name: '', quantityMilli: l.quantityMilli, unitCode: 'C62', unitPriceCents: l.unitPriceCents, discountBp: l.discountBp, taxCategory: code.category, taxRate: code.rate };
    }),
  });
}

interface Props {
  lines: InvoiceLineDraft[];
  setLines: (update: (lines: InvoiceLineDraft[]) => InvoiceLineDraft[]) => void;
  discount: string;
  setDiscount: (value: string) => void;
  taxMode: TaxMode;
  fixUnits?: boolean;
}

export function InvoiceLinesEditor({ lines, setLines, discount, setDiscount, taxMode, fixUnits = false }: Props) {
  const { units, symbols, defaultUnit } = useUnits();
  const small = taxMode === 'small_business';
  const p = parsed(lines);
  const discountBp = parseBp(discount || '0');
  const totals = useMemo(
    () => invoiceTotalsOf(invoiceLinesBody(lines).map((l) => ({ ...l, taxCode: l.taxCode })), discountBp ?? 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(lines.map((l) => [l.qty, l.price, l.discount, l.taxCode])), discountBp],
  );

  useEffect(() => {
    if (!units?.length || !fixUnits) return;
    setLines((ls) => ls.map((l) => (symbols.includes(l.unit) || l.itemId ? l : { ...l, unit: defaultUnit })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, fixUnits, lines.length]);

  const setLine = (key: string, patch: Partial<InvoiceLineDraft>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const move = (index: number, delta: -1 | 1) =>
    setLines((ls) => {
      const next = [...ls];
      const target = index + delta;
      if (target < 0 || target >= next.length) return ls;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });

  return (
    <>
      <div className="offer-lines">
        {lines.map((l, i) => {
          const pl = p[i]!;
          return (
            <div key={l.key} className="offer-line">
              <span className="offer-line__pos" aria-hidden="true">{i + 1}</span>
              <div className="offer-line__main">
                <input className="input" value={l.title} onChange={(e) => setLine(l.key, { title: e.target.value })} placeholder="Bezeichnung" aria-label={`Bezeichnung Position ${i + 1}`} maxLength={200} />
                <textarea className="input textarea textarea--line" value={l.description} onChange={(e) => setLine(l.key, { description: e.target.value })} placeholder="Beschreibung (freiwillig)" aria-label={`Beschreibung Position ${i + 1}`} maxLength={2000} />
              </div>
              <label className="offer-line__cell">
                <span>Menge</span>
                <input className="input num" inputMode="decimal" value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} aria-invalid={pl.quantityMilli === null || pl.quantityMilli <= 0} />
              </label>
              <label className="offer-line__cell">
                <span>Einheit</span>
                <select className="input" value={l.unit} onChange={(e) => setLine(l.key, { unit: e.target.value })} aria-invalid={!!units && !symbols.includes(l.unit)}>
                  {!symbols.includes(l.unit) && <option value={l.unit}>{l.unit ? `${l.unit} (nicht in der Einrichtung)` : 'Bitte wählen'}</option>}
                  {units?.map((u) => <option key={u.id} value={u.symbol} title={u.name}>{u.symbol}</option>)}
                </select>
              </label>
              <label className="offer-line__cell">
                <span>Einzelpreis €</span>
                <input className="input num" inputMode="decimal" value={l.price} onChange={(e) => setLine(l.key, { price: e.target.value })} placeholder="0,00" aria-invalid={pl.unitPriceCents === null} />
              </label>
              <label className="offer-line__cell">
                <span>Rabatt %</span>
                <input className="input num" inputMode="decimal" value={l.discount} onChange={(e) => setLine(l.key, { discount: e.target.value })} placeholder="0" />
              </label>
              <label className="offer-line__cell">
                <span>Steuer</span>
                <select className="input" value={small ? 'KU' : l.taxCode} onChange={(e) => setLine(l.key, { taxCode: e.target.value })} disabled={small}>
                  {TAX_CODES.filter((c) => !c.inputOnly && (small ? c.code === 'KU' : c.code !== 'KU')).map((c) => <option key={c.code} value={c.code} title={c.label}>{c.rate > 0 ? `${c.rate} %` : c.label}</option>)}
                </select>
              </label>
              <div className="offer-line__amount">
                <span className="muted small">Betrag</span>
                <strong>{formatCents(totals.lines[i]?.lineTotalCents ?? 0)}</strong>
              </div>
              <div className="offer-line__tools">
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Position ${i + 1} nach oben`}>↑</button>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => move(i, 1)} disabled={i === lines.length - 1} aria-label={`Position ${i + 1} nach unten`}>↓</button>
                <button type="button" className="btn btn--danger btn--sm" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} aria-label={`Position ${i + 1} entfernen`}>Entfernen</button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="offer-editor__add">
        <ProductPicker onPick={(pr) => setLines((ls) => [...ls.filter((l) => l.title || l.price), draftFromProduct(pr, taxMode)])} />
        <button type="button" className="btn btn--secondary" onClick={() => setLines((ls) => [...ls, emptyInvoiceLine(taxMode, defaultUnit)])}>Freie Position</button>
      </div>

      <div className="offer-totals">
        <label className="field offer-totals__discount">
          <span>Rabatt auf alles %</span>
          <input className="input num" inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" aria-invalid={discountBp === null} />
        </label>
        <dl>
          {totals.allowanceTotalCents !== 0 && (
            <>
              <dt>Summe der Positionen</dt>
              <dd>{formatCents(totals.lineTotalCents)}</dd>
              <dt>abzüglich {formatPercent(discountBp ?? 0)}</dt>
              <dd>– {formatCents(totals.allowanceTotalCents)}</dd>
            </>
          )}
          <dt>Netto</dt>
          <dd>{formatCents(totals.taxBasisCents)}</dd>
          {!small && totals.breakdown.filter((g) => g.rate > 0).map((g) => (
            <Row key={`${g.category}${g.rate}`} label={`${g.rate} % USt.`} value={formatCents(g.taxCents)} />
          ))}
          <dt className="offer-totals__gross">Gesamt brutto</dt>
          <dd className="offer-totals__gross">{formatCents(totals.grandCents)}</dd>
        </dl>
        {small && <p className="muted small">Kleinunternehmer nach § 19 UStG: keine Umsatzsteuer.</p>}
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}
